import React, { createContext, useContext, useState, useEffect } from 'react'
import { useAuth } from './AuthContext'
import { trackSignUp } from '../utils/analytics'

const AuthModalContext = createContext()

// Common country codes for phone login
const COUNTRY_CODES = [
  { code: '+1', name: 'US' },
  { code: '+1', name: 'Canada' },
  { code: '+44', name: 'UK' },
  { code: '+61', name: 'Australia' },
  { code: '+33', name: 'France' },
  { code: '+49', name: 'Germany' },
  { code: '+39', name: 'Italy' },
  { code: '+34', name: 'Spain' },
  { code: '+81', name: 'Japan' },
  { code: '+86', name: 'China' },
  { code: '+91', name: 'India' },
  { code: '+52', name: 'Mexico' },
  { code: '+55', name: 'Brazil' },
  { code: '+82', name: 'S. Korea' },
  { code: '+31', name: 'Netherlands' },
  { code: '+41', name: 'Switzerland' },
  { code: '+46', name: 'Sweden' },
  { code: '+47', name: 'Norway' },
  { code: '+45', name: 'Denmark' },
  { code: '+358', name: 'Finland' },
  { code: '+353', name: 'Ireland' },
  { code: '+64', name: 'New Zealand' },
  { code: '+65', name: 'Singapore' },
  { code: '+852', name: 'Hong Kong' },
  { code: '+971', name: 'UAE' },
  { code: '+966', name: 'Saudi Arabia' },
  { code: '+27', name: 'South Africa' },
  { code: '+7', name: 'Russia' },
  { code: '+48', name: 'Poland' },
  { code: '+43', name: 'Austria' },
  { code: '+32', name: 'Belgium' },
]

export function useAuthModal() {
  return useContext(AuthModalContext)
}

export function AuthModalProvider({ children }) {
  const { user, login, signUp, loginWithGoogle, sendPhoneVerification, confirmPhoneCode } = useAuth()
  const [showModal, setShowModal] = useState(false)
  const [isSignUp, setIsSignUp] = useState(false)
  const [loginMethod, setLoginMethod] = useState('email') // 'email' or 'phone'
  
  // Email state
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  
  // Phone state
  const [countryCode, setCountryCode] = useState('+1')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [verificationCode, setVerificationCode] = useState('')
  const [codeSent, setCodeSent] = useState(false)
  
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const handleAuthSuccess = () => {
    const redirectTo = sessionStorage.getItem('voyabay_post_login_redirect')
    if (redirectTo) {
      sessionStorage.removeItem('voyabay_post_login_redirect')
      window.location.assign(redirectTo)
      return
    }
    window.location.reload()
  }

  // Open modal functions
  const openLogin = () => {
    // Save current URL before opening modal
    sessionStorage.setItem('voyabay_search_url', window.location.search)
    setIsSignUp(false)
    setShowModal(true)
    setError('')
  }

  const openSignUp = () => {
    // Save current URL before opening modal
    sessionStorage.setItem('voyabay_search_url', window.location.search)
    setIsSignUp(true)
    setShowModal(true)
    setError('')
  }

  const closeModal = () => {
    setShowModal(false)
    setEmail('')
    setPassword('')
    setConfirmPassword('')
    setPhoneNumber('')
    setVerificationCode('')
    setCodeSent(false)
    setLoginMethod('email')
    setError('')
  }

  // Auto-close modal when user logs in
  useEffect(() => {
    if (user && showModal) {
      closeModal()
      sessionStorage.removeItem('voyabay_search_url')
    }
  }, [user, showModal])

  // Handle email form submit
  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    
    // Validate password confirmation for sign up
    if (isSignUp && password !== confirmPassword) {
      setError('Passwords do not match')
      setLoading(false)
      return
    }
    
    try {
      if (isSignUp) {
        await signUp(email, password)
        trackSignUp('email')
      } else {
        await login(email, password)
      }
      // Modal will auto-close via useEffect.
      handleAuthSuccess()
    } catch (err) {
      setError(err.message || (isSignUp ? 'Sign up failed' : 'Login failed'))
      sessionStorage.removeItem('voyabay_search_url')
    } finally {
      setLoading(false)
    }
  }

  // Handle sending phone verification code
  const handleSendCode = async (e) => {
    e.preventDefault()
    setError('')
    
    const cleanNumber = phoneNumber.replace(/\D/g, '')
    const formattedPhone = countryCode + cleanNumber
    
    if (cleanNumber.length < 6) {
      setError('Please enter a valid phone number.')
      return
    }
    
    setLoading(true)

    try {
      await sendPhoneVerification(formattedPhone, 'modal-recaptcha-container')
      setCodeSent(true)
    } catch (err) {
      console.error('Phone verification error:', err)
      if (err.code === 'auth/invalid-phone-number') {
        setError('Invalid phone number. Please check the format.')
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many requests. Please try again later.')
      } else if (err.code === 'auth/invalid-app-credential') {
        setError('Phone authentication is not available. Please use email or Google sign-in.')
      } else if (err.code === 'auth/operation-not-allowed') {
        setError('Phone authentication is not enabled. Please use email or Google sign-in.')
      } else {
        setError('Failed to send verification code. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  // Handle verifying phone code
  const handleVerifyCode = async (e) => {
    e.preventDefault()
    setError('')
    
    if (verificationCode.length !== 6) {
      setError('Please enter the 6-digit code.')
      return
    }
    
    setLoading(true)

    try {
      await confirmPhoneCode(verificationCode)
      handleAuthSuccess()
    } catch (err) {
      console.error('Code verification error:', err)
      if (err.code === 'auth/invalid-verification-code') {
        setError('Invalid verification code. Please try again.')
      } else if (err.code === 'auth/code-expired') {
        setError('Code expired. Please request a new one.')
        setCodeSent(false)
        setVerificationCode('')
      } else {
        setError('Failed to verify code. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  // Handle Google login
  const handleGoogleLogin = async () => {
    setLoading(true)
    setError('')
    
    try {
      await loginWithGoogle()
      if (isSignUp) {
        trackSignUp('google')
      }
      // Modal will auto-close via useEffect.
      handleAuthSuccess()
    } catch (err) {
      setError(err.message || 'Google sign-in failed')
      sessionStorage.removeItem('voyabay_search_url')
    } finally {
      setLoading(false)
    }
  }

  const value = {
    showModal,
    isSignUp,
    email,
    password,
    error,
    loading,
    setEmail,
    setPassword,
    setIsSignUp,
    openLogin,
    openSignUp,
    closeModal,
    handleSubmit,
    handleGoogleLogin
  }

  return (
    <AuthModalContext.Provider value={value}>
      {children}
      
      {/* reCAPTCHA container for phone auth */}
      <div id="modal-recaptcha-container"></div>
      
      {/* Global Auth Modal */}
      {showModal && (
        <div className="auth-modal-overlay" onClick={closeModal}>
          <div className="auth-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>×</button>
            <h2>{isSignUp ? 'Create Account' : 'Sign In'}</h2>
            <p className="modal-subtitle">
              {isSignUp 
                ? 'Create a free account to unlock member rates'
                : 'Sign in to unlock exclusive member rates'
              }
            </p>
            
            {/* Login Method Toggle */}
            <div className="modal-method-toggle">
              <button
                type="button"
                className={`modal-toggle-btn ${loginMethod === 'email' ? 'active' : ''}`}
                onClick={() => { setLoginMethod('email'); setError(''); setCodeSent(false); }}
              >
                Email
              </button>
              <button
                type="button"
                className={`modal-toggle-btn ${loginMethod === 'phone' ? 'active' : ''}`}
                onClick={() => { setLoginMethod('phone'); setError(''); }}
              >
                Phone
              </button>
            </div>
            
            {error && <div className="login-error">{error}</div>}
            
            {/* Email Form */}
            {loginMethod === 'email' && (
              <form onSubmit={handleSubmit}>
                <input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                {isSignUp && (
                  <input
                    type="password"
                    placeholder="Confirm Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                )}
                <button type="submit" className="login-submit-btn" disabled={loading}>
                  {loading ? 'Please wait...' : (isSignUp ? 'Create Account' : 'Sign In')}
                </button>
              </form>
            )}
            
            {/* Phone Form - Enter Number */}
            {loginMethod === 'phone' && !codeSent && (
              <form onSubmit={handleSendCode}>
                <div className="phone-input-wrapper">
                  <select
                    className="country-code-select"
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                  >
                    {COUNTRY_CODES.map((c, i) => (
                      <option key={`${c.code}-${i}`} value={c.code}>
                        {c.name} ({c.code})
                      </option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value.replace(/[^\d\s()-]/g, ''))}
                    required
                  />
                </div>
                <button type="submit" className="login-submit-btn" disabled={loading}>
                  {loading ? 'Sending...' : 'Send Verification Code'}
                </button>
              </form>
            )}
            
            {/* Phone Form - Enter Code */}
            {loginMethod === 'phone' && codeSent && (
              <form onSubmit={handleVerifyCode}>
                <input
                  type="text"
                  placeholder="Enter 6-digit code"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                  required
                />
                <p className="code-hint">Code sent to {countryCode} {phoneNumber}</p>
                <button type="submit" className="login-submit-btn" disabled={loading}>
                  {loading ? 'Verifying...' : 'Verify Code'}
                </button>
                <button
                  type="button"
                  className="resend-btn"
                  onClick={() => { setCodeSent(false); setVerificationCode(''); }}
                >
                  Didn't receive a code? Try again
                </button>
              </form>
            )}
            
            <div className="modal-divider">
              <span>or</span>
            </div>
            
            <button 
              type="button" 
              className="google-signin-btn" 
              onClick={handleGoogleLogin}
              disabled={loading}
            >
              <svg viewBox="0 0 24 24" width="18" height="18">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>
            
            <div className="modal-footer">
              {isSignUp ? (
                <span>Already have an account? <button type="button" onClick={() => setIsSignUp(false)}>Sign In</button></span>
              ) : (
                <span>Don't have an account? <button type="button" onClick={() => setIsSignUp(true)}>Sign Up Free</button></span>
              )}
            </div>
          </div>
        </div>
      )}
    </AuthModalContext.Provider>
  )
}
